/**
 * Deadlines for the upload path — the thing that was missing.
 *
 * ⛔ THE BUG THIS EXISTS TO KILL. Every request in `cloud-upload.ts` and
 * `cloud-artifact-upload.ts` called bare `fetch(url, init)` with no `signal`,
 * so each one inherited undici's defaults — 300s to receive headers, 300s
 * between body chunks — and each sat inside a 3-attempt retry loop. One stalled
 * connection therefore held the process for roughly 3 × 5 minutes plus backoff.
 * The upload runs inside Playwright's `onEnd`, which the runner must await
 * before it can exit, so the visible symptom was "the tests passed and then
 * Studio hung", with Ctrl+C doing nothing: an in-flight request with no abort
 * signal is not cancelled by a signal handler. Observed at 11m49s on a real run.
 *
 * ⛔ AND THE CONFIGURED TIMEOUT NEVER REACHED IT. `timeout` (default 30s) was
 * threaded into `cloud-auth.ts` — the token exchange and the health check — and
 * nowhere else. A tester who set it believed they had bounded uploading; they
 * had bounded the handshake.
 *
 * Three tools, deliberately separate:
 *
 *   · `withTimeout` — one request, one AbortController, always cleared.
 *   · `putDeadlineMs` — a transfer's budget scales with its size, because a
 *     200 MB trace legitimately takes longer than a 2 KB JSON POST and a flat
 *     cap would abandon healthy uploads on slow links.
 *   · `Budget` — the phase-level circuit breaker: once the whole upload has
 *     spent its allowance, later requests fail immediately rather than each
 *     paying its own timeout. Ten artifacts × one stall each is the difference
 *     between one late run and an unusable app.
 */

/** What the upload phase gets in total, when nothing else is configured. */
export const DEFAULT_UPLOAD_BUDGET_MS = 120_000;

/** Floor and ceiling for a single presigned PUT, whatever the file size says. */
export const PUT_MIN_MS = 30_000;
export const PUT_MAX_MS = 600_000;

/** Assumed throughput for sizing a transfer's deadline: 1 MB/s is slow enough
 *  to cover a hotel wifi and fast enough that a wedged socket still gives up. */
const ASSUMED_BYTES_PER_MS = 1_000;

/**
 * Run one fetch under a deadline.
 *
 * The timer is cleared in `finally` — a pending `setTimeout` keeps Node's event
 * loop alive, which in a reporter that has just finished its work is its own
 * flavour of "the process won't exit".
 */
export async function withTimeout(
  url: string,
  init: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), Math.max(1, timeoutMs));
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/** How long one presigned PUT may take, from the size it declares. */
export function putDeadlineMs(sizeBytes: number, base: number): number {
  const scaled = base + Math.ceil(Math.max(0, sizeBytes) / ASSUMED_BYTES_PER_MS);
  return Math.min(PUT_MAX_MS, Math.max(PUT_MIN_MS, scaled));
}

/**
 * The upload phase's total allowance.
 *
 * ⛔ `remaining()` returning 0 is a REFUSAL, not a hint: callers must stop
 * rather than start a request with no time left, because a request started at
 * the boundary still pays its own full timeout — which is exactly how "one
 * stalled artifact" became "ten stalled artifacts".
 */
export class Budget {
  private readonly deadline: number;

  constructor(totalMs: number = DEFAULT_UPLOAD_BUDGET_MS, now: number = Date.now()) {
    this.deadline = now + Math.max(0, totalMs);
  }

  remaining(now: number = Date.now()): number {
    return Math.max(0, this.deadline - now);
  }

  expired(now: number = Date.now()): boolean {
    return this.remaining(now) === 0;
  }

  /** The budget for one request: never more than what the phase has left. */
  slice(requestMs: number, now: number = Date.now()): number {
    return Math.min(requestMs, this.remaining(now));
  }
}
